import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import noFeedbackIcon from "@assets/images/shareroom/no-feedback.svg";
import { Group } from "@domain/group/group";
import { useShareroomFeedbacks } from "@domain/group/hooks";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Title } from "@ui/components/containers/title";
import { DeleteIcon } from "@ui/components/icons";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Row, Stack } from "@ui/components/layout";
import { useModalTools } from "@ui/modals/modalContext";
import { alpha, desktop, gap, mobile } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { FeedbackModal } from "./feedbackModal";
import { FeedbackView } from "./feedbackView";

interface ShareroomFeedbackProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
}

export const ShareroomFeedback: React.FC<ShareroomFeedbackProps> = ({ shareroom, ...htmlProps }) => {
	const { createModal } = useModalTools();
	const { result: feedbacks, loading, fetchMore } = useShareroomFeedbacks(shareroom.id);

	const isActiveMember =
		(shareroom.license === "PREMIUM" && shareroom.subscriptionStatus === "ACTIVE") ||
		(shareroom.license === "FREE" && shareroom.userRole === "MEMBER");

	const [postButtonVisible, setPostButtonVisible] = useState(false);

	useEffect(() => {
		const listener = (): void => {
			setPostButtonVisible(window.scrollY > 576);
		};
		document.addEventListener("scroll", listener, true);
		return () => document.removeEventListener("scroll", listener, true);
	}, []);

	return (
		<Container {...htmlProps}>
			<Row align="center" justify="space-between">
				<Title>{I18n.t("feedback.title")}</Title>
				{isActiveMember ? (
					<RoundedPlusButton
						onClick={() => createModal((close) => <FeedbackModal shareroom={shareroom} close={close} />)}
						label={I18n.t("shareroom.add_review.button")}
					/>
				) : null}
			</Row>

			{loading ? (
				<ClipLoader />
			) : (
				<>
					{feedbacks.length ? (
						<InfiniteScroll loading={loading} onEndReached={fetchMore}>
							{feedbacks.map((feedback) => (
								<FeedbackView key={feedback.user.id} feedback={feedback} shareroom={shareroom} />
							))}
						</InfiniteScroll>
					) : (
						<Row justify="center">
							<Stack>
								<img src={noFeedbackIcon} />
								<EmptyMessage>{I18n.t("shareroom.review.empty")}</EmptyMessage>
							</Stack>
						</Row>
					)}
				</>
			)}

			{isActiveMember ? (
				<MobileCreatePostButton
					visible={postButtonVisible}
					onClick={() => createModal((close) => <FeedbackModal shareroom={shareroom} close={close} />)}
				>
					<DeleteIcon />
				</MobileCreatePostButton>
			) : null}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	padding: 30px 40px;
	${gap(60)};
`;

const MobileCreatePostButton = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>`
	display: flex;
	pointer-events: none;
	opacity: 0;
	position: fixed;
	bottom: 28px;
	right: 20px;
	justify-content: center;
	align-items: center;
	width: 50px;
	height: 50px;
	border-radius: 25px;
	background-color: ${({ theme }) => theme.colors.secondary};
	box-shadow: 0 2px 14px 0 ${({ theme }) => alpha(theme.colors.secondary, 0.6)};
	z-index: ${Z.menu};
	cursor: pointer;

	@media ${desktop} {
		right: 100px;
	}

	${({ visible }) =>
		visible
			? css`
					opacity: 1;
					pointer-events: auto;
				`
			: css`
					opacity: 0;
					pointer-events: none;
				`}

	transition: opacity 0.2s ease-in;

	svg {
		width: 27px;
		height: 26px;
		transform: rotate(45deg);
		path {
			fill: ${({ theme }) => theme.colors.white};
		}
	}
`;
const EmptyMessage = styled.div`
	font-weight: 400;
	font-size: 16px;
	line-height: 26px;
	color: rgba(1, 1, 1, 0.5);
	margin-top: 40px;
	@media ${mobile} {
		font-size: 13px;
	}
`;
